import type { StateCreator } from "zustand";

import type { Coupon } from "@/shared/domains";
import type { User } from "@/shared/domains/users";
import type { ImperialBmiInputs, UserAttributes } from "@/shared/models";
import type { OnboardingUser } from "../models";

type ContactDetails = Omit<OnboardingUser, "firstName" | "lastName">;

export interface UserSlice {
  personalDetails?: Omit<User, "email">;
  contactDetails?: ContactDetails;
  userAttributes?: UserAttributes;
  bmiInputs?: ImperialBmiInputs;
  token?: OnboardingUser["token"];
  initialDiscountCode?: Coupon["code"];
  isCallCenter: boolean;

  getUser: () => OnboardingUser | undefined;
  setPersonalDetails: (personalDetails: Omit<User, "email">) => void;
  setContactDetails: (contactDetails: ContactDetails) => void;
  patchPersonalDetails: (personalDetails: Partial<Omit<User, "email">>) => void;
  patchContactDetails: (contactDetails: Partial<ContactDetails>) => void;
  setUserAttributes: (userAttributes: UserAttributes) => void;
  setBmiInputs: (bmiInputs: ImperialBmiInputs) => void;
  setUserToken: (token: OnboardingUser["token"]) => void;
  setInitialDiscountCode: (initialDiscountCode: Coupon["code"]) => void;
  setIsCallCenter: (isCallCenter: boolean) => void;

  reset: VoidFunction;
}

export const createUserSlice: StateCreator<UserSlice, [], [], UserSlice> = (
  set,
  get,
) => ({
  isCallCenter: false,
  getUser: () => {
    const { personalDetails, contactDetails } = get();

    if (!personalDetails || !contactDetails) {
      return undefined;
    }

    return { ...personalDetails, ...contactDetails };
  },
  setPersonalDetails: (personalDetails) => set({ personalDetails }),
  setContactDetails: (contactDetails) => set({ contactDetails }),
  patchPersonalDetails: (personalDetails) =>
    set((state) => ({
      personalDetails: {
        ...state.personalDetails,
        ...personalDetails,
      } as Omit<User, "email">,
    })),
  patchContactDetails: (contactDetails) =>
    set((state) => ({
      contactDetails: {
        ...state.contactDetails,
        ...contactDetails,
      } as ContactDetails,
    })),
  setUserAttributes: (userAttributes) => set({ userAttributes }),
  setBmiInputs: (bmiInputs) => set({ bmiInputs }),
  setUserToken: (token) => set({ token }),
  setInitialDiscountCode: (initialDiscountCode) => set({ initialDiscountCode }),
  setIsCallCenter: (isCallCenter) => set({ isCallCenter }),

  reset: () =>
    set({
      personalDetails: undefined,
      contactDetails: undefined,
      userAttributes: undefined,
      bmiInputs: undefined,
      token: undefined,
      initialDiscountCode: undefined,
      isCallCenter: false,
    }),
});
